/**
 * Hobonichi launch — Klaviyo signup helper.
 *
 * Exposes window.hobonichiSubscribe(email, properties), a small Promise
 * wrapper around the Klaviyo client subscriptions API, and auto-binds any
 * form[data-hobonichi-signup] found on the page (email input + submit
 * button). Config comes from window.hobonichiKlaviyoConfig = { publicKey,
 * listId }, set via an inline script tag rendered by the hero section from
 * the global "Hobonichi launch" theme settings. No dependencies.
 *
 * A form may carry data-properties='{"hobonichi_match":"cousin"}' (any JSON
 * object) to merge extra profile properties into the subscribe call — used
 * by the quiz result screen to record the shopper's matched line.
 *
 * Idempotent: if this file is loaded more than once on the same page (the
 * hero AND the quiz both request it), the second load is a no-op — it
 * never re-binds already-bound forms.
 */
(function () {
  'use strict';

  if (window.hobonichiSubscribe) {
    // Already bound by an earlier load of this asset on the same page
    // (e.g. both the hero and the quiz sections load hobonichi-signup.js).
    // Bail out so forms are never double-bound.
    return;
  }

  function hobonichiSubscribe(email, properties) {
    return new Promise(function (resolve, reject) {
      try {
        var config = window.hobonichiKlaviyoConfig || {};

        if (!config.publicKey || !config.listId || !email) {
          reject(new Error('Hobonichi signup: missing Klaviyo config or email'));
          return;
        }

        var body = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email, properties: properties || {} }
                }
              },
              custom_source: 'Hobonichi launch page'
            },
            relationships: {
              list: { data: { type: 'list', id: config.listId } }
            }
          }
        };

        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(config.publicKey), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            if (response.status >= 200 && response.status < 300) {
              resolve(response);
            } else {
              reject(new Error('Klaviyo subscription failed: ' + response.status));
            }
          })
          .catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  window.hobonichiSubscribe = hobonichiSubscribe;

  // GA4 event passthrough. snippets/ga4-tag.liquid defines gtag on every
  // storefront page; the guard keeps this file standalone — without gtag,
  // events just no-op.
  function track(name, params) {
    if (typeof gtag === 'function') gtag('event', name, params);
  }

  function bindForm(form) {
    var button = form.querySelector('button');
    var emailInput = form.querySelector('input[type="email"]');
    var errorEl = form.querySelector('[data-hobonichi-signup-error]');
    var successMessage = form.getAttribute('data-success-message') || 'Thanks, we will let you know.';
    var buttonText = button ? button.textContent : '';

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var email = emailInput ? emailInput.value.trim() : '';

      if (!email) {
        if (errorEl) {
          errorEl.textContent = 'Please enter your email address.';
          errorEl.hidden = false;
        }
        return;
      }

      if (errorEl) errorEl.hidden = true;
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      // Optional data-properties="{...}" attribute lets a page (e.g. the
      // quiz result screen) pass extra Klaviyo profile properties — such as
      // { hobonichi_match: line_key } — without needing a dedicated form
      // per line. Read fresh at submit time (not cached at bind time):
      // the quiz doesn't know the shopper's match until well after this
      // form is bound, so it sets the attribute just before the form
      // becomes visible. Invalid/missing JSON just means no extra
      // properties.
      var extraProperties = {};
      var rawProperties = form.getAttribute('data-properties');
      if (rawProperties) {
        try {
          extraProperties = JSON.parse(rawProperties) || {};
        } catch (error) {
          extraProperties = {};
        }
      }

      hobonichiSubscribe(email, extraProperties)
        .then(function () {
          var params = { source: extraProperties.hobonichi_signup_source || 'unknown' };
          if (extraProperties.hobonichi_line) params.line = extraProperties.hobonichi_line;
          if (extraProperties.hobonichi_match) params.match = extraProperties.hobonichi_match;
          track('signup_submit', params);

          var success = document.createElement('p');
          success.className = 'hobonichi-signup__success';
          success.textContent = successMessage;
          form.replaceWith(success);
        })
        .catch(function () {
          if (errorEl) {
            errorEl.textContent = 'Something went wrong. Please try again.';
            errorEl.hidden = false;
          }
          if (button) {
            button.disabled = false;
            button.textContent = buttonText;
          }
        });
    });
  }

  try {
    var forms = document.querySelectorAll('form[data-hobonichi-signup]');
    forms.forEach(bindForm);
  } catch (error) {
    /* no-op — defensive: a signup form failing to bind should never break the page */
  }

  // Notify anchors (#hobonichi-notify, from the builder band and FAQ) scroll to
  // the hero signup form and open the keyboard by focusing its email input.
  // Scoped to #hobonichi-notify so the quiz form (also data-hobonichi-signup)
  // is never targeted; falls back to the wrapper if the hero form has already
  // been replaced by its success message.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href="#hobonichi-notify"]');
    if (!link) return;
    var form = document.querySelector('#hobonichi-notify form[data-hobonichi-signup]');
    var target = form || document.getElementById('hobonichi-notify');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var input = form ? form.querySelector('input[type="email"]') : null;
    if (input) input.focus({ preventScroll: true });
  });
})();
